/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {SystemViewHistoryIcon} from '@ion/dls/src/icons/current/SystemViewHistoryIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {TertiaryIconButton, TertiaryIconButtonProps} from './TertiaryIconButton';

export interface ViewHistoryIconButtonProps extends TertiaryIconButtonProps {}

const useStyles = makeStyles(() => ({
  root : {
    boxSizing       : 'border-box',
    display         : 'flex',
    flex            : '0 0 auto',
    width           : '216px',
    height          : '32px',
    minHeight       : '32px',
    padding         : '4px 8px',
    gap             : '8px',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('pastelTeal')}`,
    borderRadius    : '8px !important',
    '&:disabled'    : {
      backgroundColor : dlsColor('grey8'),
      borderColor     : dlsColor('grey7')
    }
  }
}));

/**
 * The ViewHistoryIconButton is a TertiaryIconButton that displays a clock
 * history icon with "View History" text, laid out as the Command Bar
 * "Mixed" variant's wider labeled pill (icon left of text, bordered,
 * darkTeal on white) - unlike the 4 icon-only Command Bar actions, which
 * stay in TertiaryIconButton's plain/unstyled form. Other TertiaryIconButton
 * props may also be specified (except "icon"/"label"/"iconDirection"/
 * "className", which are fixed).
 *
 * ```typescript
 *
 *   import React from 'react';
 *   import {ViewHistoryIconButton} from '@ion/dls/src/components/buttons/';
 *   ...
 *
 *   function MyComponent {
 *     return (
 *       <ViewHistoryIconButton onClick={onViewHistory} />
 *     );
 *   }
 *
 * ```
 *
 */
export const ViewHistoryIconButton = (props: ViewHistoryIconButtonProps) => {
  const classes   = useStyles();
  const iconColor = props.disabled ? dlsColor('grey4') : dlsColor('darkTeal');

  const labelStyle = useMemo(() => (
    props.disabled ? undefined : {
      color         : dlsColor('darkTeal'),
      fontSize      : '14px',
      fontWeight    : '500',
      lineHeight    : '20px',
      letterSpacing : '0'
    }
  ), [props.disabled]);

  return (
    <TertiaryIconButton {...props}
      className={classes.root}
      iconDirection='left'
      labelStyle={labelStyle}
      icon={<SystemViewHistoryIcon size={IconSize.medium} color={iconColor} />}
      label={translate('tracking:buttons.viewHistory')} />
  );
};

export default React.memo(ViewHistoryIconButton);
